import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { jobSalary } from "@/lib/jobSalary";
import { jobLocation } from "@/lib/jobLocation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, SPONSORSHIP_LABELS } from "@/lib/api";
import { MapPin } from "lucide-react";
import { useLatestValue } from "@/hooks/useLatestValue";

export default function Jobs({ sponsorshipMode = false }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({
    q: params.get("q") || "", town: "", industry: "", salary_min: "",
    sponsorship: sponsorshipMode ? "sponsorship_confirmed" : "", outside_uk: "", graduate: "", country: "",
  });
  const [loading, setLoading] = useState(true);
  const filtersRef = useLatestValue(filters);

  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);
  const loadVersion = useRef(0);
  const load = useCallback(async (targetPage = 1) => {
    const version = ++loadVersion.current;
    setPage(targetPage); setLoading(true); setLoadError("");
    const clean = Object.fromEntries(Object.entries(filtersRef.current).filter(([, v]) => v !== "" && v !== null));
    try {
      const { data } = await api.get("/jobs", { params: { ...clean, page: targetPage, size: 12 } });
      if (version !== loadVersion.current) return;
      setData({ ...data, items: data.items || [], total: data.total || 0 });
    } catch (error) {
      if (version === loadVersion.current) setLoadError(applicationErrorMessage(error, "Unable to load vacancies. Please try again."));
    } finally { if (version === loadVersion.current) setLoading(false); }
  }, [filtersRef]);

  const invalidateLoad = useCallback(() => { ++loadVersion.current; }, []);
  useEffect(() => { load(1); return invalidateLoad; }, [load, invalidateLoad, filters.sponsorship, filters.outside_uk, filters.graduate]);

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid={sponsorshipMode ? "sponsorship-jobs-page" : "jobs-page"}>
      <p className="overline">{sponsorshipMode ? "Sponsorship-friendly vacancies" : "Recruitment marketplace"}</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-semibold">
        {sponsorshipMode ? "Vacancies with recorded sponsorship evidence" : "Search vacancies"}
      </h1>
      {sponsorshipMode && (
        <p className="mt-3 max-w-3xl text-sm text-slate-600" data-testid="sponsorship-disclaimer">
          Every label stores its evidence and source. A sponsor licence does not mean an organisation is
          currently recruiting or will sponsor a particular candidate.
        </p>
      )}

      <form className="mt-8 grid gap-3 md:grid-cols-5" onSubmit={(e) => { e.preventDefault(); setParams({ q: filters.q }); load(1); }}>
        <input data-testid="job-search-input" className="field md:col-span-2" placeholder="Job title, skill or employer"
          value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <input data-testid="job-town-input" className="field" placeholder="Town or city"
          value={filters.town} onChange={(e) => setFilters({ ...filters, town: e.target.value })} />
        <input data-testid="job-country-input" className="field" placeholder="Country"
          value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
        <input data-testid="job-salary-input" className="field" type="number" placeholder="Minimum salary (£)"
          value={filters.salary_min} onChange={(e) => setFilters({ ...filters, salary_min: e.target.value })} />
        <button data-testid="job-search-submit" className="btn btn-primary justify-center">Search</button>
      </form>

      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <select data-testid="filter-sponsorship" className="field w-auto" value={filters.sponsorship}
          onChange={(e) => setFilters({ ...filters, sponsorship: e.target.value })}>
          <option value="">Any sponsorship position</option>
          {Object.entries(SPONSORSHIP_LABELS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
        </select>
        <select data-testid="filter-outside-uk" className="field w-auto" value={filters.outside_uk}
          onChange={(e) => setFilters({ ...filters, outside_uk: e.target.value })}>
          <option value="">Inside or outside the UK</option>
          <option value="true">Suitable for applicants outside the UK</option>
          <option value="false">Applicants inside the UK</option>
        </select>
        <select data-testid="filter-graduate" className="field w-auto" value={filters.graduate}
          onChange={(e) => setFilters({ ...filters, graduate: e.target.value })}>
          <option value="">All experience levels</option>
          <option value="true">Graduate roles</option>
        </select>
      </div>

      <p className="mt-6 text-sm text-slate-500" data-testid="jobs-result-count">
        {loading ? "Loading vacancies…" : loadError ? "Vacancies could not be loaded" : `${data.total} vacancies found`}
      </p>

      {loadError && <div className="mt-4"><p role="alert" className="text-sm text-red-700">{loadError}</p><button type="button" className="btn btn-ghost mt-3" onClick={() => load(page)}>Retry loading</button></div>}
      {!loading && !loadError && data.total === 0 && <p className="mt-4 text-sm text-slate-600">No vacancies match these filters. Try a different search.</p>}
      <div className="mt-4 grid gap-4">
        {!loading && !loadError && data.items.map((j) => {
          const label = SPONSORSHIP_LABELS[j.sponsorship_assessment?.label] || SPONSORSHIP_LABELS.not_established;
          return (
            <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-card-${j.id}`} className="card p-5 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-[#0f172a]">{j.title}</h2>
                <p className="text-sm text-slate-600">{j.company_name} · {j.industry}</p>
                <p className="mt-2 text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {jobLocation(j)}
                  {j.occupation_code && <span className="ml-3">Occupation code {j.occupation_code}</span>}
                </p>
              </div>
              <div className="sm:text-right">
                <p className="font-semibold">{jobSalary(j)}</p>
                <span className={`pill mt-2 ${label.cls}`} data-testid={`sponsorship-label-${j.id}`}>{label.text}</span>
              </div>
            </Link>
          );
        })}
        {!loading && data.items.length === 0 && <p className="text-sm text-slate-500">No vacancies are currently published or match the selected filters.</p>}
      </div>
      {!loading && !loadError && data.total > 12 && <nav aria-label="Vacancy result pages" className="mt-6 flex items-center gap-4">
        <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
        <span className="text-sm">Page {page} of {Math.ceil(data.total / 12)}</span>
        <button type="button" className="btn btn-ghost" disabled={page >= Math.ceil(data.total / 12)} onClick={() => load(page + 1)}>Next</button>
      </nav>}
    </div>
  );
}

